import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adminApi,
  type AdminCategory,
  type AdminOrder,
  type AdminProduct,
  type AdminStats,
  type AdminStockItem,
  type AdminUser,
  type AdminUserDetails,
  type AdminReview,
  type ProductType,
} from '../api/marketplace';
import { ChatDesk } from '../features/chat/ChatDesk';
import { haptic } from '../lib/telegram';
import { apiErrorMessage } from '../lib/api';
import { StarIcon } from '../features/tabs/icons';
import { ErrorState, Loader } from '../components/States';
import { SearchFilters, matchesQuery } from '../components/SearchFilters';
import { RatingStars } from '../features/reviews/ReviewsBlock';
import { formatTopUpRange } from '../lib/top-up';
import './AdminPage.css';

type Section = 'products' | 'chats' | 'orders' | 'refunds' | 'users' | 'reviews' | 'stats';

const SECTIONS: Array<{ id: Section; label: string }> = [
  { id: 'products', label: 'Товары' },
  { id: 'chats', label: 'Чаты' },
  { id: 'orders', label: 'Заказы' },
  { id: 'refunds', label: 'Возвраты' },
  { id: 'users', label: 'Пользователи' },
  { id: 'reviews', label: 'Отзывы' },
  { id: 'stats', label: 'Сводка' },
];

const TYPE_LABELS: Record<ProductType, string> = {
  KEY: 'Ключ',
  ACCOUNT: 'Аккаунт',
  LICENSE: 'Лицензия',
  SUBSCRIPTION: 'Подписка',
  TEXT: 'Услуга',
};

const ORDER_STATUS_LABELS: Record<string, string> = {
  PENDING: 'оформляется',
  AWAITING_PAYMENT: 'ждёт оплаты',
  PAID: 'оплачен',
  DELIVERED: 'выдан',
  CANCELLED: 'отменён',
  FAILED: 'ошибка',
  REFUNDED: 'возврат',
};

/**
 * Админка внутри Mini App.
 *
 * Вкладка монтируется только для роли ADMIN (см. App.tsx), но на этом защита не
 * строится: каждый запрос всё равно проверяется бэкендом по роли из JWT. Скрытая
 * вкладка — это удобство, а не граница доступа.
 */
export function AdminPage() {
  const [section, setSection] = useState<Section>('products');

  return (
    <div className="admin">
      <header className="admin__header">
        <h1 className="page-title">Админка</h1>
      </header>

      <div className="admin__tabs" role="tablist">
        {SECTIONS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={section === item.id}
            className={`chip${section === item.id ? ' chip--active' : ''}`}
            onClick={() => setSection(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {section === 'products' ? <ProductsSection /> : null}
      {section === 'chats' ? <ChatDesk mode="admin" /> : null}
      {section === 'orders' ? <OrdersSection /> : null}
      {section === 'refunds' ? <RefundsSection /> : null}
      {section === 'users' ? <UsersSection /> : null}
      {section === 'reviews' ? <ReviewsSection /> : null}
      {section === 'stats' ? <StatsSection /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Товары и сток
// ---------------------------------------------------------------------------

function ProductsSection() {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isCreating, setCreating] = useState(false);
  const [isCreatingTopUp, setCreatingTopUp] = useState(false);
  /** Товар, для которого открыта форма загрузки ключей. */
  const [stockFor, setStockFor] = useState<string | null>(null);
  const [editFor, setEditFor] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [sort, setSort] = useState('newest');

  const load = useCallback(async () => {
    setError(null);

    try {
      const [productsResponse, categoriesResponse] = await Promise.all([
        adminApi.products(),
        adminApi.categories(),
      ]);

      setProducts(productsResponse.items);
      setCategories(categoriesResponse.items);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить товары');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const removeProduct = async (product: AdminProduct): Promise<void> => {
    if (!window.confirm(`Удалить «${product.title}» из витрины? Заказы и выданные ключи сохранятся в истории.`)) {
      return;
    }

    setBusyId(product.id);

    try {
      await adminApi.deleteProduct(product.id);
      haptic('success');
      setNotice(`Товар «${product.title}» удалён`);
      await load();
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось удалить товар');
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (product: AdminProduct): Promise<void> => {
    setBusyId(product.id);

    try {
      await adminApi.updateProduct(product.id, { isActive: !product.isActive });
      await load();
      haptic('success');
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось изменить товар');
    } finally {
      setBusyId(null);
    }
  };

  const visible = useMemo(() => {
    const filtered = products.filter((product) => {
      if (product.isTopUp) return false;
      if (
        !matchesQuery(
          [product.title, product.slug, product.subtitle, product.category?.title, product.category?.slug],
          search,
        )
      ) {
        return false;
      }

      if (typeFilter !== 'all' && product.type !== typeFilter) return false;
      if (categoryFilter !== 'all' && (product.category?.slug ?? 'none') !== categoryFilter) return false;
      if (activeFilter === 'on' && !product.isActive) return false;
      if (activeFilter === 'off' && product.isActive) return false;
      const available = sellingCount(product);
      if (stockFilter === 'in' && available <= 0) return false;
      if (stockFilter === 'out' && available > 0) return false;
      if (stockFilter === 'sale' && product.type !== 'TEXT' && product.stock.SOLD <= 0) return false;
      if (stockFilter === 'sale' && product.type === 'TEXT') return false;
      return true;
    });

    return filtered.sort((left, right) => {
      if (sort === 'title') return left.title.localeCompare(right.title, 'ru');
      if (sort === 'price_asc') return left.priceStars - right.priceStars;
      if (sort === 'price_desc') return right.priceStars - left.priceStars;
      if (sort === 'stock') return sellingCount(right) - sellingCount(left);
      return 0;
    });
  }, [products, search, typeFilter, categoryFilter, activeFilter, stockFilter, sort]);

  const topUps = useMemo(() => {
    return products.filter((product) => {
      if (!product.isTopUp) return false;
      return matchesQuery(
        [product.title, product.slug, product.subtitle, product.category?.title, product.category?.slug],
        search,
      );
    });
  }, [products, search]);

  if (isLoading) return <Loader />;
  if (error && products.length === 0) return <ErrorState message={error} onRetry={() => void load()} />;

  return (
    <>
      {error ? <p className="admin__error">{error}</p> : null}
      {notice ? <p className="admin__notice">{notice}</p> : null}

      <h2 className="admin__section-title">Пополнения</h2>
      <p className="admin__hint">
        Все виды пополнения на витрине в одном блоке. Покупатель выбирает сервис вкладкой, у каждого свои суммы и
        комиссия.
      </p>
      <button
        type="button"
        className="admin__primary"
        onClick={() => {
          setCreatingTopUp((value) => !value);
          setCreating(false);
        }}
      >
        {isCreatingTopUp ? 'Отменить' : '+ Добавить пополнение'}
      </button>

      {isCreatingTopUp ? (
        <ProductForm
          categories={categories}
          forceTopUp
          onSaved={async (message) => {
            setCreatingTopUp(false);
            setNotice(message);
            await load();
          }}
        />
      ) : null}

      {topUps.map((product) => (
        <article key={product.id} className="admin-card">
          <div className="admin-card__head">
            <div>
              <p className="admin-card__title">{product.title}</p>
              <p className="admin-card__meta">
                комиссия {((product.commissionBps ?? 0) / 100).toString().replace(/\.0$/, '')}%
                {' · '}
                {formatTopUpRange(product)}
                {!product.isActive ? ' · скрыто' : ''}
                {!product.stockListed ? ' · нет в наличии' : ''}
              </p>
            </div>
          </div>
          <div className="admin-card__actions">
            <button
              type="button"
              className="admin__secondary"
              onClick={() => {
                setEditFor(editFor === product.id ? null : product.id);
                setStockFor(null);
              }}
            >
              {editFor === product.id ? 'Закрыть' : 'Изменить'}
            </button>
            <button
              type="button"
              className="admin__secondary"
              disabled={busyId === product.id}
              onClick={() => void toggleActive(product)}
            >
              {product.isActive ? 'Скрыть' : 'Показать'}
            </button>
            <button
              type="button"
              className="admin__danger"
              disabled={busyId === product.id}
              onClick={() => void removeProduct(product)}
            >
              Удалить
            </button>
          </div>
          {editFor === product.id ? (
            <ProductForm
              categories={categories}
              product={product}
              forceTopUp
              onSaved={async (message) => {
                setEditFor(null);
                setNotice(message);
                await load();
              }}
            />
          ) : null}
        </article>
      ))}

      <h2 className="admin__section-title">Товары</h2>
      <button type="button" className="admin__primary" onClick={() => {
        setCreating((value) => !value);
        setCreatingTopUp(false);
      }}>
        {isCreating ? 'Отменить' : '+ Новый товар'}
      </button>

      <SearchFilters
        search={search}
        onSearch={setSearch}
        placeholder="Название, slug или категория"
        filters={[
          {
            id: 'sort',
            value: sort,
            onChange: setSort,
            options: [
              { value: 'newest', label: 'Сначала новые' },
              { value: 'title', label: 'По названию' },
              { value: 'price_asc', label: 'Дешевле' },
              { value: 'price_desc', label: 'Дороже' },
              { value: 'stock', label: 'Больше в наличии' },
            ],
          },
          {
            id: 'type',
            value: typeFilter,
            onChange: setTypeFilter,
            options: [
              { value: 'all', label: 'Все типы' },
              ...Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })),
            ],
          },
          {
            id: 'category',
            value: categoryFilter,
            onChange: setCategoryFilter,
            options: [
              { value: 'all', label: 'Все категории' },
              { value: 'none', label: 'Без категории' },
              ...categories.map((item) => ({ value: item.slug, label: item.title })),
            ],
          },
          {
            id: 'active',
            value: activeFilter,
            onChange: setActiveFilter,
            options: [
              { value: 'all', label: 'Все карточки' },
              { value: 'on', label: 'В витрине' },
              { value: 'off', label: 'Скрытые' },
            ],
          },
          {
            id: 'stock',
            value: stockFilter,
            onChange: setStockFilter,
            options: [
              { value: 'all', label: 'Любой сток' },
              { value: 'in', label: 'Есть ключи' },
              { value: 'out', label: 'Пустой сток' },
              { value: 'sale', label: 'Были продажи' },
            ],
          },
        ]}
      />

      {isCreating ? (
        <ProductForm
          categories={categories}
          onSaved={async (message) => {
            setCreating(false);
            setNotice(message);
            await load();
          }}
        />
      ) : null}

      {products.length === 0 ? (
        <p className="admin__empty">Товаров пока нет. Создай первый — и загрузи к нему ключи.</p>
      ) : null}

      {products.length > 0 && visible.length === 0 ? (
        <p className="admin__empty">Нет товаров по этим фильтрам</p>
      ) : null}

      {visible.map((product) => (
        <article key={product.id} className="admin-card">
          <div className="admin-card__head">
            {product.coverUrl ? <img className="admin-card__cover" src={product.coverUrl} alt="" /> : null}
            <div>
              <p className="admin-card__title">{product.title}</p>
              <p className="admin-card__meta">
                {product.slug}
                {product.category ? ` · ${product.category.title}` : ' · без категории'}
              </p>
            </div>
            <span className="admin-card__price">
              <StarIcon size={12} /> {product.priceStars}
              {product.priceRub ? ` · ${product.priceRub} ₽` : ''}
            </span>
          </div>

          <div className="stock">
            <span className="stock__cell">
              в наличии <b>{product.stockQuantity == null ? 'без лимита' : product.stockQuantity}</b>
            </span>
            <span className="stock__cell">
              {product.stockListed && (product.stockQuantity == null || product.stockQuantity > 0)
                ? 'продаём'
                : 'нет в наличии'}
            </span>
            {product.type !== 'TEXT' ? (
              <>
                <span className="stock__cell">
                  ключей <b>{product.stock.AVAILABLE}</b>
                </span>
                <span className="stock__cell">
                  в резерве <b>{product.stock.RESERVED}</b>
                </span>
                <span className="stock__cell">
                  продано <b>{product.stock.SOLD}</b>
                </span>
                {product.stock.DISABLED > 0 ? (
                  <span className="stock__cell">
                    брак <b>{product.stock.DISABLED}</b>
                  </span>
                ) : null}
              </>
            ) : null}
          </div>

          {!product.isActive ? <p className="admin-card__flag">Скрыт из каталога</p> : null}

          <div className="admin-card__actions">
            {product.type === 'TEXT' ? null : (
              <button
                type="button"
                className="admin__secondary"
                onClick={() => {
                  setStockFor(stockFor === product.id ? null : product.id);
                  setEditFor(null);
                }}
              >
                {stockFor === product.id ? 'Закрыть' : 'Ключи'}
              </button>
            )}
            <button
              type="button"
              className="admin__secondary"
              onClick={() => {
                setEditFor(editFor === product.id ? null : product.id);
                setStockFor(null);
              }}
            >
              {editFor === product.id ? 'Закрыть' : 'Изменить'}
            </button>
            <button
              type="button"
              className="admin__secondary"
              disabled={busyId === product.id}
              onClick={() => void toggleActive(product)}
            >
              {product.isActive ? 'Скрыть' : 'Показать'}
            </button>
            <button
              type="button"
              className="admin__danger"
              disabled={busyId === product.id}
              onClick={() => void removeProduct(product)}
            >
              Удалить
            </button>
          </div>

          {editFor === product.id ? (
            <ProductForm
              categories={categories}
              product={product}
              onSaved={async (message) => {
                setEditFor(null);
                setNotice(message);
                await load();
              }}
            />
          ) : null}

          {stockFor === product.id && product.type !== 'TEXT' ? (
            <StockManager
              productId={product.id}
              onChanged={async (message) => {
                setNotice(message);
                await load();
              }}
            />
          ) : null}
        </article>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------

const KEY_STATUS_LABELS: Record<string, string> = {
  AVAILABLE: 'в наличии',
  RESERVED: 'резерв',
  SOLD: 'продан',
  DISABLED: 'брак',
};

interface StockManagerProps {
  productId: string;
  onChanged: (message: string) => Promise<void>;
}

function StockManager({ productId, onChanged }: StockManagerProps) {
  const [items, setItems] = useState<AdminStockItem[]>([]);
  const [bulk, setBulk] = useState('');
  const [replacements, setReplacements] = useState<Record<string, string>>({});
  const [isLoading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const response = await adminApi.stock(productId);
    setItems(response.items);
  }, [productId]);

  useEffect(() => {
    setLoading(true);
    void reload()
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить ключи');
      })
      .finally(() => setLoading(false));
  }, [reload]);

  const addKeys = async (): Promise<void> => {
    const secrets = bulk
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (secrets.length === 0) {
      setError('Вставь хотя бы один ключ');
      return;
    }

    setBusyId('add');
    setError(null);

    try {
      const result = await adminApi.addStock(productId, secrets);
      haptic('success');
      setBulk('');
      await reload();
      await onChanged(
        result.duplicates > 0
          ? `Добавлено ${result.added}, пропущено дублей: ${result.duplicates}`
          : `Добавлено ключей: ${result.added}`,
      );
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить ключи');
    } finally {
      setBusyId(null);
    }
  };

  const replace = async (id: string): Promise<void> => {
    const secret = replacements[id]?.trim() ?? '';

    if (!secret) {
      setError('Введи новый ключ');
      return;
    }

    setBusyId(id);
    setError(null);

    try {
      await adminApi.replaceKey(id, secret);
      haptic('success');
      setReplacements((current) => ({ ...current, [id]: '' }));
      await reload();
      await onChanged('Ключ заменён');
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось заменить ключ');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string): Promise<void> => {
    if (!window.confirm('Удалить этот ключ из стока?')) return;

    setBusyId(id);
    setError(null);

    try {
      await adminApi.deleteKey(id);
      haptic('success');
      await reload();
      await onChanged('Ключ удалён');
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось удалить ключ');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="admin-form">
      <p className="admin-form__label">Ключи в карточке</p>
      {isLoading ? <p className="admin-card__meta">Загружаю…</p> : null}

      {items.map((item) => {
        const canEdit = item.status === 'AVAILABLE' || item.status === 'DISABLED';

        return (
          <div key={item.id} className="stock-row">
            <p className="stock-row__meta">
              <b>{item.payloadHint ?? '…'}</b> · {KEY_STATUS_LABELS[item.status] ?? item.status}
            </p>
            {canEdit ? (
              <div className="stock-row__edit">
                <input
                  className="admin-form__input"
                  value={replacements[item.id] ?? ''}
                  onChange={(event) =>
                    setReplacements((current) => ({ ...current, [item.id]: event.target.value }))
                  }
                  placeholder="Новый ключ"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className="admin__secondary"
                  disabled={busyId === item.id}
                  onClick={() => void replace(item.id)}
                >
                  Заменить
                </button>
                <button
                  type="button"
                  className="admin__danger"
                  disabled={busyId === item.id}
                  onClick={() => void remove(item.id)}
                >
                  Удалить
                </button>
              </div>
            ) : null}
          </div>
        );
      })}

      {items.length === 0 && !isLoading ? <p className="admin-card__meta">Ключей пока нет</p> : null}

      <label className="admin-form__label" htmlFor={`stock-${productId}`}>
        Добавить ключи, по одному на строку
      </label>
      <textarea
        id={`stock-${productId}`}
        className="admin-form__input admin-form__input--area selectable"
        rows={3}
        value={bulk}
        onChange={(event) => setBulk(event.target.value)}
        placeholder={'STEAM-AAAA-BBBB\nSTEAM-CCCC-DDDD'}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
      />
      {error ? <p className="admin__error">{error}</p> : null}
      <button type="button" className="admin__primary" disabled={busyId !== null} onClick={() => void addKeys()}>
        {busyId === 'add' ? 'Загружаю…' : 'Добавить ключи'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface ProductFormProps {
  categories: AdminCategory[];
  product?: AdminProduct;
  forceTopUp?: boolean;
  onSaved: (message: string) => Promise<void>;
}

function ProductForm({ categories, product, forceTopUp, onSaved }: ProductFormProps) {
  const isEdit = Boolean(product);
  const [title, setTitle] = useState(product?.title ?? '');
  const [slug, setSlug] = useState(product?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [subtitle, setSubtitle] = useState(product?.subtitle ?? '');
  const [price, setPrice] = useState(product ? String(product.priceStars) : '');
  const [compareAt, setCompareAt] = useState(product?.compareAtStars ? String(product.compareAtStars) : '');
  const [priceRub, setPriceRub] = useState(product?.priceRub ? String(product.priceRub) : '');
  const [compareAtRub, setCompareAtRub] = useState(product?.compareAtRub ? String(product.compareAtRub) : '');
  const [type, setType] = useState<ProductType>(product?.type ?? (forceTopUp ? 'TEXT' : 'KEY'));
  const [description, setDescription] = useState(product?.description ?? '');
  const [categorySlug, setCategorySlug] = useState(product?.category?.slug ?? '');
  const [instructions, setInstructions] = useState(product?.deliveryInstructions ?? '');
  const [maxPerOrder, setMaxPerOrder] = useState(
    product && product.maxPerOrder < 1_000_000 ? String(product.maxPerOrder) : '',
  );
  const [stockQuantity, setStockQuantity] = useState(
    product && product.stockQuantity != null ? String(product.stockQuantity) : '',
  );
  const [stockListed, setStockListed] = useState(product?.stockListed ?? true);
  const [isTopUp, setIsTopUp] = useState(Boolean(product?.isTopUp || forceTopUp));
  const [commissionPercent, setCommissionPercent] = useState(
    product ? String(((product.commissionBps ?? 0) / 100).toString().replace(/\.0$/, '')) : '0',
  );
  const [topUpMin, setTopUpMin] = useState(
    product?.topUpMinRub && product.topUpMinRub > 0 ? String(product.topUpMinRub) : '',
  );
  const [topUpMax, setTopUpMax] = useState(product?.topUpMaxRub ? String(product.topUpMaxRub) : '');
  const [tributeUrl, setTributeUrl] = useState(product?.tributeUrl ?? '');
  const [coverUrl, setCoverUrl] = useState(product?.coverUrl ?? '');
  const [newCategory, setNewCategory] = useState('');
  const [editions, setEditions] = useState<
    Array<{
      id?: string;
      title: string;
      description: string;
      priceStars: string;
      priceRub: string;
      compareAtStars: string;
      compareAtRub: string;
    }>
  >(
    (product?.editions ?? []).map((edition) => ({
      id: edition.id,
      title: edition.title,
      description: edition.description ?? '',
      priceStars: String(edition.priceStars),
      priceRub: edition.priceRub ? String(edition.priceRub) : '',
      compareAtStars: edition.compareAtStars ? String(edition.compareAtStars) : '',
      compareAtRub: edition.compareAtRub ? String(edition.compareAtRub) : '',
    })),
  );
  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fieldId = product?.id ?? 'new';

  const pickCover = async (file: File | undefined): Promise<void> => {
    if (!file) return;

    if (file.size > 2_500_000) {
      setError('Фото тяжелее 2.5 МБ');
      return;
    }

    const dataUrl = await readFileAsDataUrl(file);
    setBusy(true);
    setError(null);

    try {
      const uploaded = await adminApi.uploadCover(dataUrl);
      setCoverUrl(uploaded.url);
      haptic('success');
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить фото');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (): Promise<void> => {
    let priceStars = Number(price);
    const maxRaw = maxPerOrder.trim();
    const max = maxRaw ? Number(maxRaw) : 1_000_000;

    if (!title.trim() || !slug.trim()) {
      setError('Название и slug обязательны');
      return;
    }

    if (!Number.isInteger(max) || max < 1) {
      setError('Максимум в заказе — целое число от 1, либо пусто без лимита');
      return;
    }

    const stockRaw = stockQuantity.trim();
    const stock = stockRaw ? Number(stockRaw) : null;

    if (stock !== null && (!Number.isInteger(stock) || stock < 0)) {
      setError('Количество — целое число от 0, либо пусто без лимита');
      return;
    }

    const compareAtStars = compareAt.trim() ? Number(compareAt) : null;
    let rub = priceRub.trim() ? Number(priceRub) : null;
    const oldRub = compareAtRub.trim() ? Number(compareAtRub) : null;

    if (compareAtStars !== null && (!Number.isInteger(compareAtStars) || compareAtStars <= priceStars)) {
      setError('Старая цена в звёздах — целое число, больше текущей');
      return;
    }

    if (rub !== null && (!Number.isInteger(rub) || rub < 1)) {
      setError('Цена в рублях — целое число, минимум 1');
      return;
    }

    if (oldRub !== null && (rub === null || !Number.isInteger(oldRub) || oldRub <= rub)) {
      setError('Старая цена в рублях — целое число, больше текущей');
      return;
    }

    let topUpMinRub: number | null = null;
    let topUpMaxRub: number | null = null;
    let commissionBps = 0;

    if (isTopUp) {
      const minRaw = topUpMin.trim();
      const maxRaw = topUpMax.trim();
      topUpMinRub = minRaw ? Number(minRaw) : 0;
      topUpMaxRub = maxRaw ? Number(maxRaw) : null;
      const percent = Number(commissionPercent.replace(',', '.'));
      commissionBps = Math.round(percent * 100);

      if (minRaw && (!Number.isInteger(topUpMinRub) || topUpMinRub < 0)) {
        setError('Минимальная сумма — целое число, 0 или больше');
        return;
      }

      if (maxRaw && (!Number.isInteger(topUpMaxRub) || (topUpMaxRub ?? 0) < 1)) {
        setError('Максимальная сумма — целое число, минимум 1, пусто = без лимита');
        return;
      }

      if (topUpMaxRub != null && topUpMinRub > topUpMaxRub) {
        setError('Минимальная сумма больше максимальной');
        return;
      }

      if (!Number.isFinite(percent) || commissionBps < 0 || commissionBps > 10_000) {
        setError('Комиссия — число от 0 до 100%');
        return;
      }

      if (rub === null) rub = 1;
      if (!Number.isInteger(priceStars) || priceStars < 1) {
        priceStars = 1;
      }
    }

    const parsedEditions: Array<{
      id?: string;
      title: string;
      priceStars: number;
      priceRub: number | null;
      compareAtStars: number | null;
      compareAtRub: number | null;
      description: string | null;
    }> = [];

    if (!isTopUp) {
      for (const row of editions) {
        if (!row.title.trim() && !row.priceStars.trim() && !row.priceRub.trim()) continue;

        const editionStars = Number(row.priceStars);
        const editionRub = row.priceRub.trim() ? Number(row.priceRub) : null;
        const editionOldStars = row.compareAtStars.trim() ? Number(row.compareAtStars) : null;
        const editionOldRub = row.compareAtRub.trim() ? Number(row.compareAtRub) : null;

        if (!row.title.trim() || !Number.isInteger(editionStars) || editionStars < 1) {
          setError('У издания нужны название и цена в звёздах');
          return;
        }

        if (editionRub !== null && (!Number.isInteger(editionRub) || editionRub < 1)) {
          setError(`«${row.title.trim()}»: цена в рублях — целое число`);
          return;
        }

        parsedEditions.push({
          ...(row.id ? { id: row.id } : {}),
          title: row.title.trim(),
          description: row.description.trim() || null,
          priceStars: editionStars,
          priceRub: editionRub,
          compareAtStars: editionOldStars,
          compareAtRub: editionOldRub,
        });
      }

      if (parsedEditions.length > 0 && (!Number.isInteger(priceStars) || priceStars < 1)) {
        priceStars = parsedEditions[0]!.priceStars;
        if (rub === null) rub = parsedEditions[0]!.priceRub;
      }

      if (!Number.isInteger(priceStars) || priceStars < 1) {
        setError('Цена — целое число звёзд, минимум 1');
        return;
      }
    }

    setBusy(true);
    setError(null);

    try {
      let resolvedCategory = categorySlug;

      if (newCategory.trim()) {
        const created = await adminApi.createCategory({
          slug: slugify(newCategory),
          title: newCategory.trim(),
        });
        resolvedCategory = created.slug;
      }

      const payload = {
        slug: slugify(slug),
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        description: description.trim() || null,
        type: isTopUp ? 'TEXT' : type,
        priceStars,
        compareAtStars,
        priceRub: rub,
        compareAtRub: oldRub,
        coverUrl: coverUrl.trim() || null,
        categorySlug: resolvedCategory || null,
        deliveryInstructions: instructions.trim() || null,
        ...(maxRaw ? { maxPerOrder: max } : isEdit ? { maxPerOrder: 1_000_000 } : {}),
        stockQuantity: stock,
        stockListed,
        tributeUrl: tributeUrl.trim() || null,
        isTopUp,
        topUpMinRub,
        topUpMaxRub,
        commissionBps,
        editions: isTopUp ? [] : parsedEditions,
      };

      if (product) {
        const { slug: _slug, ...patch } = payload;
        void _slug;
        await adminApi.updateProduct(product.id, patch);
        haptic('success');
        await onSaved(`«${title.trim()}» обновлён`);
      } else {
        await adminApi.createProduct(payload);
        haptic('success');
        await onSaved(
          isTopUp
            ? `Пополнение «${title.trim()}» сохранено. Комиссия ${commissionPercent.replace(',', '.')}%.`
            : type === 'TEXT'
              ? `Товар «${title.trim()}» создан. Количество можно менять в карточке.`
              : `Товар «${title.trim()}» создан. Теперь загрузи к нему ключи.`,
        );
      }
    } catch (cause) {
      haptic('error');
      setError(apiErrorMessage(cause, 'Не удалось сохранить товар'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-form">
      <label className="admin-form__label" htmlFor={`${fieldId}-title`}>
        Название
      </label>
      <input
        id={`${fieldId}-title`}
        className="admin-form__input"
        value={title}
        onChange={(event) => {
          setTitle(event.target.value);
          if (!slugTouched) setSlug(slugify(event.target.value));
        }}
        placeholder={isTopUp || forceTopUp ? 'Пополнение Steam' : 'Steam Wallet 500'}
      />

      {!isEdit ? (
        <>
          <label className="admin-form__label" htmlFor={`${fieldId}-slug`}>
            Slug (латиница, цифры, дефис)
          </label>
          <input
            id={`${fieldId}-slug`}
            className="admin-form__input"
            value={slug}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value);
            }}
            placeholder="steam-wallet-500"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </>
      ) : null}

      {!forceTopUp ? (
        <label className="admin-form__check">
          <input
            type="checkbox"
            checked={isTopUp}
            onChange={(event) => {
              setIsTopUp(event.target.checked);
              if (event.target.checked) setType('TEXT');
            }}
          />
          Это пополнение (своя комиссия)
        </label>
      ) : null}

      {isTopUp ? (
        <>
          <label className="admin-form__label" htmlFor={`${fieldId}-commission`}>
            Комиссия, %
          </label>
          <input
            id={`${fieldId}-commission`}
            className="admin-form__input"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.1"
            value={commissionPercent}
            onChange={(event) => setCommissionPercent(event.target.value)}
            placeholder="5"
          />
          <p className="admin-form__hint">У каждого вида пополнения своя комиссия. Она сразу попадает в итоговую сумму.</p>
          <label className="admin-form__label" htmlFor={`${fieldId}-topup-min`}>
            Мин. сумма, ₽
          </label>
          <input
            id={`${fieldId}-topup-min`}
            className="admin-form__input"
            type="number"
            inputMode="numeric"
            min={0}
            value={topUpMin}
            onChange={(event) => setTopUpMin(event.target.value)}
            placeholder="Пусто — от 0"
          />
          <label className="admin-form__label" htmlFor={`${fieldId}-topup-max`}>
            Макс. сумма, ₽
          </label>
          <input
            id={`${fieldId}-topup-max`}
            className="admin-form__input"
            type="number"
            inputMode="numeric"
            min={1}
            value={topUpMax}
            onChange={(event) => setTopUpMax(event.target.value)}
            placeholder="Пусто — без лимита (∞)"
          />
        </>
      ) : null}

      {isTopUp ? null : (
        <>
      <label className="admin-form__label" htmlFor={`${fieldId}-subtitle`}>
        Короткий подзаголовок
      </label>
      <input
        id={`${fieldId}-subtitle`}
        className="admin-form__input"
        value={subtitle}
        onChange={(event) => setSubtitle(event.target.value)}
        placeholder="30 дней, регион RU"
      />

      <label className="admin-form__label" htmlFor={`${fieldId}-type`}>
        Раздел
      </label>
      <select
        id={`${fieldId}-type`}
        className="admin-form__input"
        value={type}
        onChange={(event) => setType(event.target.value as ProductType)}
      >
        <option value="KEY">Ключи</option>
        <option value="LICENSE">Лицензии</option>
        <option value="ACCOUNT">Аккаунты</option>
        <option value="SUBSCRIPTION">Подписки</option>
        <option value="TEXT">Услуги</option>
      </select>

      <label className="admin-form__label" htmlFor={`${fieldId}-stock-qty`}>
        Количество
      </label>
      <input
        id={`${fieldId}-stock-qty`}
        className="admin-form__input"
        type="number"
        inputMode="numeric"
        min={0}
        value={stockQuantity}
        onChange={(event) => setStockQuantity(event.target.value)}
        placeholder="Пусто — без лимита"
      />

      <label className="admin-form__label" htmlFor={`${fieldId}-stock-listed`}>
        Наличие
      </label>
      <select
        id={`${fieldId}-stock-listed`}
        className="admin-form__input"
        value={stockListed ? 'yes' : 'no'}
        onChange={(event) => setStockListed(event.target.value === 'yes')}
      >
        <option value="yes">В наличии</option>
        <option value="no">Нет в наличии</option>
      </select>

      <label className="admin-form__label" htmlFor={`${fieldId}-description`}>
        Описание
      </label>
      <textarea
        id={`${fieldId}-description`}
        className="admin-form__input admin-form__input--area"
        rows={4}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder="Что входит, на какой срок, как активировать"
      />

      <label className="admin-form__label" htmlFor={`${fieldId}-cover`}>
        Фото карточки
      </label>
      {coverUrl ? <img className="admin-form__preview" src={coverUrl} alt="" /> : null}
      <input
        id={`${fieldId}-cover`}
        className="admin-form__file"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(event) => void pickCover(event.target.files?.[0])}
      />
      <button type="button" className="admin__secondary" disabled={!coverUrl} onClick={() => setCoverUrl('')}>
        Убрать фото
      </button>

      <label className="admin-form__label" htmlFor={`${fieldId}-price`}>
        Цена в звёздах
      </label>
      <input
        id={`${fieldId}-price`}
        className="admin-form__input"
        type="number"
        inputMode="numeric"
        min={1}
        value={price}
        onChange={(event) => setPrice(event.target.value)}
        placeholder="150"
      />

      <label className="admin-form__label" htmlFor={`${fieldId}-rub`}>
        Цена в рублях (для Tribute)
      </label>
      <input
        id={`${fieldId}-rub`}
        className="admin-form__input"
        type="number"
        inputMode="numeric"
        min={1}
        value={priceRub}
        onChange={(event) => setPriceRub(event.target.value)}
        placeholder="Пусто — по курсу из звёзд"
      />

      <label className="admin-form__label" htmlFor={`${fieldId}-tribute`}>
        Ссылка Tribute
      </label>
      <input
        id={`${fieldId}-tribute`}
        className="admin-form__input"
        value={tributeUrl}
        onChange={(event) => setTributeUrl(event.target.value)}
        placeholder="https://t.me/tribute/app?startapp=…"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
      />

      <label className="admin-form__label" htmlFor={`${fieldId}-compare-rub`}>
        Старая цена в рублях
      </label>
      <input
        id={`${fieldId}-compare-rub`}
        className="admin-form__input"
        type="number"
        inputMode="numeric"
        min={1}
        value={compareAtRub}
        onChange={(event) => setCompareAtRub(event.target.value)}
        placeholder="Пусто — без акции в рублях"
      />

      <label className="admin-form__label" htmlFor={`${fieldId}-compare`}>
        Старая цена в звёздах
      </label>
      <input
        id={`${fieldId}-compare`}
        className="admin-form__input"
        type="number"
        inputMode="numeric"
        min={1}
        value={compareAt}
        onChange={(event) => setCompareAt(event.target.value)}
        placeholder="Пусто — без акции"
      />

      <label className="admin-form__label" htmlFor={`${fieldId}-max`}>
        Максимум в одном заказе
      </label>
      <input
        id={`${fieldId}-max`}
        className="admin-form__input"
        type="number"
        inputMode="numeric"
        min={1}
        value={maxPerOrder}
        onChange={(event) => setMaxPerOrder(event.target.value)}
        placeholder="Пусто — без лимита"
      />

      <label className="admin-form__label" htmlFor={`${fieldId}-category`}>
        Категория
      </label>
      <select
        id={`${fieldId}-category`}
        className="admin-form__input"
        value={categorySlug}
        onChange={(event) => setCategorySlug(event.target.value)}
        disabled={Boolean(newCategory.trim())}
      >
        <option value="">Без категории</option>
        {categories.map((category) => (
          <option key={category.id} value={category.slug}>
            {category.title}
          </option>
        ))}
      </select>

      <label className="admin-form__label" htmlFor={`${fieldId}-new-category`}>
        …или создать новую
      </label>
      <input
        id={`${fieldId}-new-category`}
        className="admin-form__input"
        value={newCategory}
        onChange={(event) => setNewCategory(event.target.value)}
        placeholder="Игры"
      />

          <p className="admin-form__label">Издания</p>
          <p className="admin-form__hint">Для игр: Standard, Deluxe, Premium — у каждого своя цена. Описание издания целиком подменяет текст на карточке товара. Если пусто, на карточке одна цена.</p>
          {editions.map((row, index) => (
            <div className="admin-edition" key={row.id ?? `new-${index}`}>
              <input
                className="admin-form__input"
                value={row.title}
                onChange={(event) =>
                  setEditions((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, title: event.target.value } : item,
                    ),
                  )
                }
                placeholder="Standard / Deluxe / Premium"
              />
              <textarea
                className="admin-form__input admin-form__input--area"
                rows={3}
                maxLength={4000}
                value={row.description}
                onChange={(event) =>
                  setEditions((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, description: event.target.value } : item,
                    ),
                  )
                }
                placeholder="Описание этого издания на карточке товара"
              />
              <div className="admin-edition__prices">
                <input
                  className="admin-form__input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={row.priceStars}
                  onChange={(event) =>
                    setEditions((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, priceStars: event.target.value } : item,
                      ),
                    )
                  }
                  placeholder="Stars"
                />
                <input
                  className="admin-form__input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={row.priceRub}
                  onChange={(event) =>
                    setEditions((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, priceRub: event.target.value } : item,
                      ),
                    )
                  }
                  placeholder="₽"
                />
              </div>
              <button
                type="button"
                className="admin__danger"
                onClick={() => setEditions((current) => current.filter((_, itemIndex) => itemIndex !== index))}
              >
                Убрать
              </button>
            </div>
          ))}
          <button
            type="button"
            className="admin__secondary"
            onClick={() =>
              setEditions((current) => [
                ...current,
                { title: '', description: '', priceStars: '', priceRub: '', compareAtStars: '', compareAtRub: '' },
              ])
            }
          >
            Добавить издание
          </button>
        </>
      )}

      {isTopUp ? (
        <>
          <label className="admin-form__label" htmlFor={`${fieldId}-tribute-topup`}>
            Ссылка Tribute
          </label>
          <input
            id={`${fieldId}-tribute-topup`}
            className="admin-form__input"
            value={tributeUrl}
            onChange={(event) => setTributeUrl(event.target.value)}
            placeholder="https://t.me/tribute/app?startapp=…"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <label className="admin-form__label" htmlFor={`${fieldId}-description-topup`}>
            Описание
          </label>
          <textarea
            id={`${fieldId}-description-topup`}
            className="admin-form__input admin-form__input--area"
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Как пополнить, что написать в заявке"
          />
          <label className="admin-form__label" htmlFor={`${fieldId}-stock-listed-topup`}>
            Наличие
          </label>
          <select
            id={`${fieldId}-stock-listed-topup`}
            className="admin-form__input"
            value={stockListed ? 'yes' : 'no'}
            onChange={(event) => setStockListed(event.target.value === 'yes')}
          >
            <option value="yes">В наличии</option>
            <option value="no">Нет в наличии</option>
          </select>
        </>
      ) : null}

      {isTopUp ? null : (
        <>
      <label className="admin-form__label" htmlFor={`${fieldId}-instructions`}>
        Инструкция покупателю
      </label>
      <textarea
        id={`${fieldId}-instructions`}
        className="admin-form__input admin-form__input--area"
        rows={2}
        value={instructions}
        onChange={(event) => setInstructions(event.target.value)}
        placeholder="Steam → «Пополнить кошелёк» → «Использовать код»."
      />
        </>
      )}

      {error ? <p className="admin__error">{error}</p> : null}

      <button type="button" className="admin__primary" disabled={isBusy} onClick={() => void submit()}>
        {isBusy
          ? 'Сохраняю…'
          : isEdit
            ? 'Сохранить'
            : isTopUp
              ? 'Создать пополнение'
              : 'Создать товар'}
      </button>
    </div>
  );
}

function sellingCount(product: AdminProduct): number {
  if (!product.stockListed) return 0;
  if (product.stockQuantity != null && product.stockQuantity <= 0) return 0;
  if (product.type !== 'TEXT' && product.stock.AVAILABLE > 0) {
    return product.stockQuantity == null
      ? product.stock.AVAILABLE
      : Math.min(product.stock.AVAILABLE, product.stockQuantity);
  }
  return product.stockQuantity ?? Number.MAX_SAFE_INTEGER;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'));
    reader.readAsDataURL(file);
  });
}

/** "Steam Wallet 500 ₽" → "steam-wallet-500". Бэкенд принимает только такой вид. */
function slugify(value: string): string {
  const translit: Record<string, string> = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y',
    к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f',
    х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  };

  return value
    .toLowerCase()
    .split('')
    .map((char) => translit[char] ?? char)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

// ---------------------------------------------------------------------------
// Заказы
// ---------------------------------------------------------------------------

function OrdersSection() {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('newest');

  const load = useCallback(async () => {
    setError(null);

    try {
      const response = await adminApi.orders();
      setOrders(response.items);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить заказы');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const refund = async (order: AdminOrder): Promise<void> => {
    // Возврат звёзд необратим, а кнопка находится рядом с остальными — без
    // подтверждения случайный тап стоил бы реальных денег.
    if (!window.confirm(`Вернуть ${order.totalAmount} ★ по заказу ${order.number}? Отменить это нельзя.`)) {
      return;
    }

    setBusyId(order.id);

    try {
      const result = await adminApi.refund(order.id, 'Возврат из админки');
      haptic('success');
      setNotice(
        `Заказ ${order.number}: ${result.moneyNote}. Ключей отключено: ${result.restocked}`,
      );
      await load();
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось сделать возврат');
    } finally {
      setBusyId(null);
    }
  };

  const visible = useMemo(() => {
    const filtered = orders.filter((order) => {
      if (statusFilter !== 'all' && order.status !== statusFilter) return false;
      return matchesQuery(
        [
          order.number,
          order.user.username,
          order.user.displayName,
          order.paymentProvider,
          ...order.items.map((item) => item.titleSnapshot),
          ...order.items.map((item) => item.payloadHintSnapshot),
          ...order.items.map((item) => item.inventoryItem?.payloadHint),
        ],
        search,
      );
    });

    return filtered.sort((left, right) => {
      if (sort === 'oldest') return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
      if (sort === 'amount_desc') return right.totalAmount - left.totalAmount;
      if (sort === 'amount_asc') return left.totalAmount - right.totalAmount;
      return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
    });
  }, [orders, search, statusFilter, sort]);

  if (isLoading) return <Loader />;
  if (error && orders.length === 0) return <ErrorState message={error} onRetry={() => void load()} />;

  return (
    <>
      {error ? <p className="admin__error">{error}</p> : null}
      {notice ? <p className="admin__notice">{notice}</p> : null}

      <SearchFilters
        search={search}
        onSearch={setSearch}
        placeholder="Номер заказа, покупатель или товар"
        filters={[
          {
            id: 'status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: 'all', label: 'Все статусы' },
              ...Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => ({ value, label })),
            ],
          },
          {
            id: 'sort',
            value: sort,
            onChange: setSort,
            options: [
              { value: 'newest', label: 'Сначала новые' },
              { value: 'oldest', label: 'Сначала старые' },
              { value: 'amount_desc', label: 'Сумма ↓' },
              { value: 'amount_asc', label: 'Сумма ↑' },
            ],
          },
        ]}
      />

      {orders.length === 0 ? <p className="admin__empty">Заказов пока нет</p> : null}
      {orders.length > 0 && visible.length === 0 ? <p className="admin__empty">Нет заказов по этим фильтрам</p> : null}

      {visible.map((order) => (
        <article key={order.id} className="admin-card">
          <div className="admin-card__head">
            <div>
              <p className="admin-card__title selectable">{order.number}</p>
              <p className="admin-card__meta">
                {order.user.username ? `@${order.user.username}` : (order.user.displayName ?? 'без имени')}
                {' · '}
                {new Date(order.createdAt).toLocaleString('ru-RU')}
              </p>
            </div>
            <span className="admin-card__price">
              <StarIcon size={12} /> {order.totalAmount}
            </span>
          </div>

          <p className="admin-card__meta">
            {ORDER_STATUS_LABELS[order.status] ?? order.status} ·{' '}
            {order.items
              .map((item) =>
                item.inventoryItem?.payloadHint
                  ? `${item.titleSnapshot} (${item.inventoryItem.payloadHint})`
                  : item.titleSnapshot,
              )
              .join(', ')}
          </p>

          {order.status === 'DELIVERED' ? (
            <div className="admin-card__actions">
              <button
                type="button"
                className="admin__danger"
                disabled={busyId === order.id}
                onClick={() => void refund(order)}
              >
                {busyId === order.id ? 'Возвращаю…' : 'Вернуть звёзды'}
              </button>
            </div>
          ) : null}
        </article>
      ))}
    </>
  );
}

function buyerLabel(order: AdminOrder): string {
  return order.user.username ? `@${order.user.username}` : (order.user.displayName ?? 'без имени');
}

function itemHint(item: AdminOrder['items'][number]): string {
  return item.inventoryItem?.payloadHint ?? item.payloadHintSnapshot ?? '';
}

function itemLabel(item: AdminOrder['items'][number]): string {
  const hint = itemHint(item);
  return hint ? `${item.titleSnapshot} · ключ ${hint}` : item.titleSnapshot;
}

function openItems(order: AdminOrder): AdminOrder['items'] {
  return order.items.filter((item) => !item.refundedAt);
}

// ---------------------------------------------------------------------------
// Возвраты
// ---------------------------------------------------------------------------

function RefundsSection() {
  const [pending, setPending] = useState<AdminOrder[]>([]);
  const [done, setDone] = useState<AdminOrder[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reason, setReason] = useState('Ключ не подошёл');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('newest');

  const load = useCallback(async () => {
    setError(null);

    try {
      const response = await adminApi.refunds();
      setPending(response.pending);
      setDone(response.done);
      setPicked((current) => {
        const next = { ...current };
        for (const order of response.pending) {
          const open = openItems(order).map((item) => item.id);
          const previous = current[order.id]?.filter((id) => open.includes(id)) ?? [];
          next[order.id] = previous.length > 0 ? previous : open;
        }
        return next;
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить возвраты');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const matchRefund = (order: AdminOrder): boolean => {
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    return matchesQuery(
      [
        order.number,
        order.user.username,
        order.user.displayName,
        order.cancelReason,
        ...order.items.map((item) => item.titleSnapshot),
        ...order.items.map((item) => item.payloadHintSnapshot),
        ...order.items.map((item) => item.inventoryItem?.payloadHint),
      ],
      search,
    );
  };

  const sortRefunds = (list: AdminOrder[]): AdminOrder[] =>
    [...list].sort((left, right) => {
      if (sort === 'oldest') return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
      if (sort === 'amount_desc') return right.totalAmount - left.totalAmount;
      return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
    });

  const visiblePending = useMemo(
    () => sortRefunds(pending.filter(matchRefund)),
    [pending, search, statusFilter, sort],
  );
  const visibleDone = useMemo(() => sortRefunds(done.filter(matchRefund)), [done, search, statusFilter, sort]);

  const toggleItem = (orderId: string, itemId: string): void => {
    setPicked((current) => {
      const selected = new Set(current[orderId] ?? []);
      if (selected.has(itemId)) selected.delete(itemId);
      else selected.add(itemId);
      return { ...current, [orderId]: [...selected] };
    });
  };

  const refund = async (order: AdminOrder): Promise<void> => {
    const why = reason.trim() || 'Возврат из админки';
    const remaining = openItems(order);
    const selected = remaining.filter((item) => (picked[order.id] ?? remaining.map((row) => row.id)).includes(item.id));

    if (selected.length === 0) {
      setError('Отметь хотя бы один ключ');
      return;
    }

    const titles = selected.map(itemLabel).join('\n');

    if (
      !window.confirm(
        `Вернуть ${selected.length} из ${remaining.length} ключей по заказу ${order.number}?\n${titles}\n\nЭти ключи станут неактивными. Отменить нельзя.`,
      )
    ) {
      return;
    }

    setBusyId(order.id);

    try {
      const result = await adminApi.refund(order.id, why, selected.map((item) => item.id));
      haptic('success');
      setNotice(`Заказ ${order.number}: ${result.moneyNote}. Ключей отключено: ${result.restocked}`);
      await load();
    } catch (cause) {
      haptic('error');
      setError(cause instanceof Error ? cause.message : 'Не удалось сделать возврат');
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) return <Loader />;
  if (error && pending.length === 0 && done.length === 0) {
    return <ErrorState message={error} onRetry={() => void load()} />;
  }

  return (
    <>
      {error ? <p className="admin__error">{error}</p> : null}
      {notice ? <p className="admin__notice">{notice}</p> : null}

      <SearchFilters
        search={search}
        onSearch={setSearch}
        placeholder="Номер заявки, покупатель или ключ"
        filters={[
          {
            id: 'stage',
            value: stage,
            onChange: setStage,
            options: [
              { value: 'all', label: 'Все заявки' },
              { value: 'pending', label: 'К возврату' },
              { value: 'done', label: 'Уже вернули' },
            ],
          },
          {
            id: 'status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: 'all', label: 'Все статусы' },
              { value: 'DELIVERED', label: 'выдан' },
              { value: 'FAILED', label: 'ошибка' },
              { value: 'PAID', label: 'оплачен' },
              { value: 'REFUNDED', label: 'возврат' },
            ],
          },
          {
            id: 'sort',
            value: sort,
            onChange: setSort,
            options: [
              { value: 'newest', label: 'Сначала новые' },
              { value: 'oldest', label: 'Сначала старые' },
              { value: 'amount_desc', label: 'Сумма ↓' },
            ],
          },
        ]}
      />

      <label className="admin-form__label" htmlFor="refund-reason">
        Причина возврата
      </label>
      <input
        id="refund-reason"
        className="admin-form__input"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Ключ не подошёл"
      />

      {stage !== 'done' ? <h2 className="admin__section-title">К возврату</h2> : null}
      {stage !== 'done' && pending.length === 0 ? <p className="admin__empty">Нет заказов, которые можно вернуть</p> : null}
      {stage !== 'done' && pending.length > 0 && visiblePending.length === 0 ? (
        <p className="admin__empty">Нет заявок по этим фильтрам</p>
      ) : null}

      {stage !== 'done' ? visiblePending.map((order) => (
        <article key={order.id} className="admin-card">
          <div className="admin-card__head">
            <div>
              <p className="admin-card__title selectable">{order.number}</p>
              <p className="admin-card__meta">
                {buyerLabel(order)} · {new Date(order.createdAt).toLocaleString('ru-RU')}
              </p>
            </div>
            <span className="admin-card__price">
              <StarIcon size={12} /> {order.totalAmount}
            </span>
          </div>

          <p className="admin-card__meta">{ORDER_STATUS_LABELS[order.status] ?? order.status}</p>
          {order.items.map((item) => {
            const closed = Boolean(item.refundedAt);
            const checked = (picked[order.id] ?? []).includes(item.id);

            return (
              <label
                key={item.id}
                className={`refund-item${closed ? ' refund-item--done' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={closed ? false : checked}
                  disabled={closed || busyId === order.id}
                  onChange={() => toggleItem(order.id, item.id)}
                />
                <span className="selectable">
                  {itemLabel(item)}
                  {closed ? ' · уже возвращён' : ` · ${item.unitPrice} ★`}
                </span>
              </label>
            );
          })}

          <div className="admin-card__actions">
            <button
              type="button"
              className="admin__danger"
              disabled={busyId === order.id || (picked[order.id] ?? []).length === 0}
              onClick={() => void refund(order)}
            >
              {busyId === order.id
                ? 'Возвращаю…'
                : (picked[order.id] ?? []).length === openItems(order).length
                  ? 'Вернуть все ключи'
                  : `Вернуть выбранные (${(picked[order.id] ?? []).length})`}
            </button>
          </div>
        </article>
      )) : null}

      {stage !== 'pending' ? <h2 className="admin__section-title">Уже вернули</h2> : null}
      {stage !== 'pending' && done.length === 0 ? <p className="admin__empty">Возвратов пока нет</p> : null}
      {stage !== 'pending' && done.length > 0 && visibleDone.length === 0 ? (
        <p className="admin__empty">Нет возвратов по этим фильтрам</p>
      ) : null}

      {stage !== 'pending' ? visibleDone.map((order) => (
        <article key={order.id} className="admin-card">
          <div className="admin-card__head">
            <div>
              <p className="admin-card__title selectable">{order.number}</p>
              <p className="admin-card__meta">
                {buyerLabel(order)} · {new Date(order.createdAt).toLocaleString('ru-RU')}
              </p>
            </div>
            <span className="admin-card__price">
              <StarIcon size={12} /> {order.totalAmount}
            </span>
          </div>
          {order.items.map((item) => (
            <p key={item.id} className="admin-card__meta">
              {itemLabel(item)}
            </p>
          ))}
          {order.cancelReason ? <p className="admin-card__meta">Причина: {order.cancelReason}</p> : null}
        </article>
      )) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Пользователи
// ---------------------------------------------------------------------------

function UsersSection() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [blockFilter, setBlockFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [openedId, setOpenedId] = useState<string | null>(null);
  const [details, setDetails] = useState<AdminUserDetails | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);

    try {
      setUsers((await adminApi.users()).items);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!openedId) {
      setDetails(null);
      setDetailsError(null);
      return;
    }

    let cancelled = false;
    setDetailsLoading(true);
    setDetailsError(null);

    adminApi
      .user(openedId)
      .then((user) => {
        if (!cancelled) setDetails(user);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setDetailsError(cause instanceof Error ? cause.message : 'Не удалось открыть профиль');
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [openedId]);

  const visible = useMemo(() => {
    const filtered = users.filter((user) => {
      if (roleFilter !== 'all' && user.role !== roleFilter) return false;
      if (blockFilter === 'on' && !user.isBlocked) return false;
      if (blockFilter === 'off' && user.isBlocked) return false;
      return matchesQuery([user.username, user.displayName, user.telegramId, user.id], search);
    });

    return filtered.sort((left, right) => {
      if (sort === 'name') {
        return (left.username ?? left.displayName ?? '').localeCompare(right.username ?? right.displayName ?? '', 'ru');
      }
      if (sort === 'orders') return right._count.orders - left._count.orders;
      if (sort === 'spent') return (right.spentStars ?? 0) - (left.spentStars ?? 0);
      if (sort === 'stars') return right.starBalance - left.starBalance;
      return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
    });
  }, [users, search, roleFilter, blockFilter, sort]);

  if (isLoading) return <Loader />;
  if (error && users.length === 0) return <ErrorState message={error} onRetry={() => void load()} />;

  return (
    <>
      {error ? <p className="admin__error">{error}</p> : null}
      <p className="admin__hint">
        Всего {users.length}{' '}
        {users.length % 100 >= 11 && users.length % 100 <= 14
          ? 'пользователей'
          : users.length % 10 === 1
            ? 'пользователь'
            : users.length % 10 >= 2 && users.length % 10 <= 4
              ? 'пользователя'
              : 'пользователей'}
      </p>

      <SearchFilters
        search={search}
        onSearch={setSearch}
        placeholder="@username, имя или Telegram ID"
        filters={[
          {
            id: 'role',
            value: roleFilter,
            onChange: setRoleFilter,
            options: [
              { value: 'all', label: 'Все роли' },
              { value: 'USER', label: 'Покупатели' },
              { value: 'ADMIN', label: 'Админы' },
            ],
          },
          {
            id: 'block',
            value: blockFilter,
            onChange: setBlockFilter,
            options: [
              { value: 'all', label: 'Все аккаунты' },
              { value: 'off', label: 'Активные' },
              { value: 'on', label: 'Заблокированные' },
            ],
          },
          {
            id: 'sort',
            value: sort,
            onChange: setSort,
            options: [
              { value: 'newest', label: 'Сначала новые' },
              { value: 'name', label: 'По имени' },
              { value: 'orders', label: 'Больше заказов' },
              { value: 'spent', label: 'Больше покупок' },
              { value: 'stars', label: 'Баланс ★' },
            ],
          },
        ]}
      />

      {users.length === 0 ? <p className="admin__empty">Пользователей пока нет</p> : null}
      {users.length > 0 && visible.length === 0 ? <p className="admin__empty">Никого не нашли</p> : null}

      {visible.map((user) => {
        const open = openedId === user.id;
        const profile = open && details?.id === user.id ? details : user;

        return (
          <article key={user.id} className="admin-card">
            <div className="admin-card__head">
              {profile.avatarUrl ? (
                <img className="admin-card__cover" src={profile.avatarUrl} alt="" />
              ) : (
                <span className="admin-user__avatar">{(profile.username ?? profile.displayName ?? '?').slice(0, 1).toUpperCase()}</span>
              )}
              <div>
                <p className="admin-card__title selectable">
                  {profile.username ? `@${profile.username}` : (profile.displayName ?? 'без имени')}
                </p>
                {profile.displayName && profile.username ? (
                  <p className="admin-card__meta selectable">{profile.displayName}</p>
                ) : null}
                <p className="admin-card__meta">
                  {profile.role === 'ADMIN' ? 'админ' : 'покупатель'}
                  {profile.languageCode ? ` · ${profile.languageCode}` : ''}
                </p>
              </div>
              <span className="admin-card__price">
                <StarIcon size={12} /> {profile.starBalance}
              </span>
            </div>

            <p className="admin-card__meta selectable">
              Telegram ID: {profile.telegramId ?? 'нет'}
            </p>
            <p className="admin-card__meta">Зарегистрирован: {formatDateTime(profile.createdAt)}</p>
            <p className="admin-card__meta">
              Последний вход: {profile.lastLoginAt ? formatDateTime(profile.lastLoginAt) : 'нет данных'}
            </p>
            <p className="admin-card__meta">
              Заказов: {profile._count.orders}
              {profile.paidOrders != null ? ` · оплачено ${profile.paidOrders}` : ''}
              {profile.spentStars != null ? ` · потрачено ${profile.spentStars} ★` : ''}
            </p>
            {profile.lastOrderAt ? (
              <p className="admin-card__meta">Последний заказ: {formatDateTime(profile.lastOrderAt)}</p>
            ) : null}
            {profile.isBlocked ? <p className="admin-card__flag">Заблокирован</p> : null}

            <div className="admin-card__actions">
              <button
                type="button"
                className="admin__secondary"
                onClick={() => setOpenedId(open ? null : user.id)}
              >
                {open ? 'Скрыть заказы' : 'Заказы'}
              </button>
            </div>

            {open ? (
              <div className="admin-user__orders">
                {detailsLoading ? <p className="admin-card__meta">Загружаю заказы…</p> : null}
                {detailsError ? <p className="admin__error">{detailsError}</p> : null}
                {details && details.orders.length === 0 ? (
                  <p className="admin-card__meta">Заказов ещё не было</p>
                ) : null}
                {details?.orders.map((order) => (
                  <p key={order.id} className="admin-card__meta">
                    <span className="selectable">{order.number}</span>
                    {' · '}
                    {ORDER_STATUS_LABELS[order.status] ?? order.status}
                    {' · '}
                    {order.totalAmount} ★
                    {' · '}
                    {formatDateTime(order.createdAt)}
                    {order.items[0] ? ` · ${order.items[0].titleSnapshot}` : ''}
                  </p>
                ))}
              </div>
            ) : null}
          </article>
        );
      })}
    </>
  );
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU');
}

// ---------------------------------------------------------------------------
// Отзывы
// ---------------------------------------------------------------------------

function ReviewsSection() {
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [hiddenFilter, setHiddenFilter] = useState('all');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setReviews((await adminApi.reviews()).items);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить отзывы');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    return reviews.filter((review) => {
      if (hiddenFilter === 'on' && !review.hiddenAt) return false;
      if (hiddenFilter === 'off' && review.hiddenAt) return false;
      return matchesQuery([review.author, review.body, review.product.title, review.user.username, review.user.displayName], search);
    });
  }, [reviews, search, hiddenFilter]);

  const hide = async (id: string, hidden: boolean): Promise<void> => {
    setBusyId(id);
    try {
      const updated = await adminApi.hideReview(id, hidden);
      setReviews((current) => current.map((item) => (item.id === id ? updated : item)));
      haptic('success');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось скрыть отзыв');
      haptic('error');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string): Promise<void> => {
    if (!window.confirm('Удалить отзыв навсегда?')) return;
    setBusyId(id);
    try {
      await adminApi.deleteReview(id);
      setReviews((current) => current.filter((item) => item.id !== id));
      haptic('success');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось удалить отзыв');
      haptic('error');
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) return <Loader />;
  if (error && reviews.length === 0) return <ErrorState message={error} onRetry={() => void load()} />;

  return (
    <>
      {error ? <p className="admin__error">{error}</p> : null}
      <p className="admin__hint">Всего {reviews.length}</p>

      <SearchFilters
        search={search}
        onSearch={setSearch}
        placeholder="Товар, автор или текст"
        filters={[
          {
            id: 'hidden',
            value: hiddenFilter,
            onChange: setHiddenFilter,
            options: [
              { value: 'all', label: 'Все отзывы' },
              { value: 'off', label: 'На витрине' },
              { value: 'on', label: 'Скрытые' },
            ],
          },
        ]}
      />

      {reviews.length === 0 ? <p className="admin__empty">Отзывов пока нет</p> : null}
      {reviews.length > 0 && visible.length === 0 ? <p className="admin__empty">Ничего не нашли</p> : null}

      {visible.map((review) => (
        <article key={review.id} className="admin-card">
          <div className="admin-card__head">
            <div>
              <p className="admin-card__title">{review.product.title}</p>
              <p className="admin-card__meta selectable">{review.author}</p>
            </div>
            <RatingStars value={review.rating} />
          </div>
          {review.body ? <p className="admin-card__meta">{review.body}</p> : <p className="admin-card__meta">Без текста</p>}
          <p className="admin-card__meta">{formatDateTime(review.createdAt)}</p>
          {review.hiddenAt ? <p className="admin-card__flag">Скрыт с витрины</p> : null}
          <div className="admin-card__actions">
            <button
              type="button"
              className="admin__secondary"
              disabled={busyId === review.id}
              onClick={() => void hide(review.id, !review.hiddenAt)}
            >
              {review.hiddenAt ? 'Вернуть' : 'Скрыть'}
            </button>
            <button
              type="button"
              className="admin__danger"
              disabled={busyId === review.id}
              onClick={() => void remove(review.id)}
            >
              Удалить
            </button>
          </div>
        </article>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Сводка
// ---------------------------------------------------------------------------

function StatsSection() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setStats(await adminApi.stats());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить сводку');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={() => void load()} />;
  if (!stats) return <Loader />;

  return (
    <>
      <div className="admin-stats">
        <div className="admin-stat">
          <p className="admin-stat__value">{stats.stars.net}</p>
          <p className="admin-stat__label">звёзд получено</p>
        </div>
        <div className="admin-stat">
          <p className="admin-stat__value">{stats.stock.AVAILABLE ?? 0}</p>
          <p className="admin-stat__label">ключей в наличии</p>
        </div>
        <div className="admin-stat">
          <p className="admin-stat__value">{stats.orders.DELIVERED ?? 0}</p>
          <p className="admin-stat__label">заказов выдано</p>
        </div>
        <div className="admin-stat">
          <p className="admin-stat__value">{stats.orders.REFUNDED ?? 0}</p>
          <p className="admin-stat__label">возвратов</p>
        </div>
        <div className="admin-stat">
          <p className="admin-stat__value">{stats.users}</p>
          <p className="admin-stat__label">пользователей</p>
        </div>
        <div className="admin-stat">
          <p className="admin-stat__value">{stats.reviews ?? 0}</p>
          <p className="admin-stat__label">отзывов на витрине</p>
        </div>
      </div>

      <article className="admin-card">
        <p className="admin-card__title">Подробно</p>
        <p className="admin-card__meta">
          Товаров: {stats.products.active} активных из {stats.products.total}
        </p>
        <p className="admin-card__meta">
          Звёзды: получено {stats.stars.earned}, возвращено {stats.stars.refunded}
        </p>
        <p className="admin-card__meta">
          Сток: в наличии {stats.stock.AVAILABLE ?? 0}, в резерве {stats.stock.RESERVED ?? 0}, продано{' '}
          {stats.stock.SOLD ?? 0}, брак {stats.stock.DISABLED ?? 0}
        </p>
        <p className="admin-card__meta">
          После возврата ключ отключается: не в наличии и не в «продано».
        </p>
        {stats.orders.FAILED ? (
          <p className="admin-card__flag">
            Заказов с ошибкой доставки: {stats.orders.FAILED} — деньги списаны, товар не выдан
          </p>
        ) : null}
      </article>
    </>
  );
}
